import { useCallback, useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { adminError, isSessionRequired } from "../lib/jamoraAdmin";
import { clampOffset, listParams } from "../lib/jamoraAnalysis";
import { useAdminSession } from "../components/jamora-admin/SessionProvider";

// Une liste d'analyse de Jamora App en pages numérotées (contrat 0.8.0) : `offset` + `limit`, tri, période, filtres, recherche.
// `phase` : loading | ready | error. Changer un filtre, la période, le tri ou la taille repart de la première page ; une réponse plus
// ancienne ne remplace jamais une plus récente ; une liste qui rétrécit ramène à la dernière page existante.
export default function useAnalysisList(path, { range = null, filters = {}, search = "", ordering = "", limit = 50, enabled = true } = {}) {
  const adminSession = useAdminSession();
  const [offset, setOffset] = useState(0);
  const [state, setState] = useState({ phase: enabled ? "loading" : "idle", items: [], total: 0, error: "", forKey: "" });
  const seq = useRef(0);
  const query = JSON.stringify({ range, filters, search, ordering, limit });
  const resetKey = `${path}|${query}`;
  const lastReset = useRef(resetKey);

  // Tout changement de requête (hors numéro de page) revient à la première page, avant la lecture.
  const effectiveOffset = lastReset.current === resetKey ? offset : 0;
  const current = `${resetKey}|${effectiveOffset}`;
  useEffect(() => {
    if (lastReset.current !== resetKey) { lastReset.current = resetKey; setOffset(0); }
  }, [resetKey]);

  const load = useCallback(async () => {
    if (!enabled || !path) return;
    const mine = ++seq.current;
    setState((s) => ({ ...s, phase: "loading", error: "", forKey: current }));
    try {
      const parsed = JSON.parse(query);
      const { data } = await api.get(`/jamora-admin${path}`, { params: listParams({ ...parsed, offset: effectiveOffset }) });
      if (mine !== seq.current) return;
      const total = typeof data.total === "number" ? data.total : data.items.length;
      const clamped = clampOffset(effectiveOffset, total, parsed.limit);
      if (clamped !== effectiveOffset) { setOffset(clamped); return; }
      setState({ phase: "ready", items: data.items, total, error: "", forKey: current });
      adminSession?.touch();
    } catch (e) {
      if (mine !== seq.current) return;
      if (isSessionRequired(e)) { adminSession?.expire(); return; }
      setState((s) => ({ ...s, phase: "error", error: adminError(e) }));
    }
  }, [enabled, path, query, effectiveOffset]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); return () => { seq.current += 1; }; }, [load]);

  const stale = enabled && state.forKey !== current;
  return {
    ...state, ...(stale ? { phase: "loading", items: [] } : {}),
    offset: effectiveOffset, limit, setOffset, reload: load,
  };
}
